'use client'

/*
  OrbitFlow Dashboard — clean, chart-led layout.
  All visualisations are line-based (area chart, sparkline, donut ring) —
  no bar charts. Stats use compact cards; the main weekly view is a smooth
  area chart with a gradient fill.
*/

import { useMemo } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import {
  Plus, Clock, Play, ArrowRight, Activity as ActivityIcon,
  Target, Flag, CalendarDays, TrendingUp,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { OrbitLoader } from '@/components/ui/orbit-loader'
import { PageHeader } from '@/components/layout/page-header'

// ─── Types ────────────────────────────────────────────────────────────────────

interface DashStats {
  assignedIssues: number
  recentActivity: any[]
  upcomingDeadlines: any[]
  timeSpentToday: number
  projectsCount: number
  projects: any[]
  onTrackProjects: number
  overdueTasks: number
  completedThisWeek: number
}

interface DashboardProps {
  stats?: Partial<DashStats>
  user?: any
  weeklyTime?: any[]
  loading?: boolean
}

const EMPTY_STATS: DashStats = {
  assignedIssues: 0, recentActivity: [], upcomingDeadlines: [],
  timeSpentToday: 0, projectsCount: 0, projects: [], onTrackProjects: 0,
  overdueTasks: 0, completedThisWeek: 0,
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function capitalize(s: string) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s }
function firstNameOf(user: any): string {
  if (!user) return 'there'
  const raw = user.firstname || (user.name ? String(user.name).split(' ')[0] : '') || user.login || ''
  return raw ? capitalize(raw) : 'there'
}
function greetingTitle(name: string): string {
  const h = new Date().getHours()
  if (h < 5)  return `Up late, ${name}.`
  if (h < 12) return `Good morning, ${name}.`
  if (h < 18) return `Good afternoon, ${name}.`
  return `Good evening, ${name}.`
}
function dateLabel(): string {
  const d = new Date()
  const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday']
  const mons = ['January','February','March','April','May','June','July','August','September','October','November','December']
  return `${days[d.getDay()]} · ${mons[d.getMonth()]} ${d.getDate()}`
}
function fmtHours(h: number): string {
  if (!h || h <= 0) return '0h'
  const hrs = Math.floor(h)
  const mins = Math.round((h - hrs) * 60)
  if (hrs === 0) return `${mins}m`
  if (mins === 0) return `${hrs}h`
  return `${hrs}h ${mins}m`
}
function dueLabel(iso?: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  const mons = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  return `${mons[d.getMonth()]} ${d.getDate()}`
}
function weekBuckets(entries: any[]): { day: string; h: number }[] {
  const days = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun']
  const totals = [0,0,0,0,0,0,0]
  for (const e of entries || []) {
    const raw = e.spent_on || e.spentOn || e.date
    if (!raw) continue
    const d = new Date(raw)
    if (isNaN(d.getTime())) continue
    let idx = d.getDay() - 1
    if (idx < 0) idx = 6
    totals[idx] += Number(e.hours ?? e.h ?? 0)
  }
  return days.map((day, i) => ({ day, h: totals[i] }))
}

// ─── Chart primitives ────────────────────────────────────────────────────────

/* Smooth (Catmull-Rom → cubic Bezier) path through N points.
   Closes to baseline on `closeAtBase` so the same path can be reused as both
   a stroke (`open`) and a filled area (`close`). */
function smoothPath(points: { x: number; y: number }[], closeAtBase?: number): string {
  if (points.length === 0) return ''
  if (points.length === 1) return `M${points[0].x},${points[0].y}`

  const tension = 0.4 // 0 = sharp angles, 1 = mushy. 0.4 reads as a relaxed editorial curve.
  let d = `M${points[0].x},${points[0].y}`
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] || points[i]
    const p1 = points[i]
    const p2 = points[i + 1]
    const p3 = points[i + 2] || p2
    const cp1x = p1.x + (p2.x - p0.x) * (tension / 6)
    const cp1y = p1.y + (p2.y - p0.y) * (tension / 6)
    const cp2x = p2.x - (p3.x - p1.x) * (tension / 6)
    const cp2y = p2.y - (p3.y - p1.y) * (tension / 6)
    d += ` C${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`
  }
  if (typeof closeAtBase === 'number') {
    d += ` L${points[points.length - 1].x},${closeAtBase} L${points[0].x},${closeAtBase} Z`
  }
  return d
}

/* Large weekly area chart — gradient fill, smooth stroke, dotted gridlines,
   point markers, today highlighted. */
function AreaChart({ data, todayIndex }: { data: { day: string; h: number }[]; todayIndex: number }) {
  const W = 760, H = 220
  const padX = 28, padTop = 16, padBottom = 36
  const innerW = W - padX * 2
  const innerH = H - padTop - padBottom

  const maxRaw = Math.max(0, ...data.map(d => d.h))
  // Give the y-axis some headroom; never collapse to a flat 0 baseline.
  const max = Math.max(maxRaw, 2) * 1.15

  const xFor = (i: number) => padX + (i / (data.length - 1)) * innerW
  const yFor = (h: number) => padTop + innerH - (h / max) * innerH
  const baseY = padTop + innerH

  const points = data.map((d, i) => ({ x: xFor(i), y: yFor(d.h) }))
  const stroke = smoothPath(points)
  const fill = smoothPath(points, baseY)

  // 4 horizontal gridlines (incl. baseline).
  const ticks = [0, max * 0.33, max * 0.66, max]

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" aria-label="Hours logged this week">
      <defs>
        <linearGradient id="of-dash-area" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="hsl(var(--primary))" stopOpacity="0.28" />
          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Horizontal gridlines */}
      {ticks.map((t, i) => {
        const y = yFor(t)
        return (
          <g key={i}>
            <line x1={padX} x2={W - padX} y1={y} y2={y}
              stroke="var(--of-border)" strokeWidth={1}
              strokeDasharray={i === 0 ? undefined : '2 4'} />
            <text x={padX - 8} y={y + 3.5} textAnchor="end"
              fontFamily="var(--of-font-mono)" fontSize={10}
              fill="var(--of-fg-3)">
              {t < 1 ? t.toFixed(1) : Math.round(t)}
            </text>
          </g>
        )
      })}

      {/* Area fill */}
      <path d={fill} fill="url(#of-dash-area)" />

      {/* Stroke on top */}
      <path d={stroke} fill="none" stroke="hsl(var(--primary))" strokeWidth={2}
        strokeLinejoin="round" strokeLinecap="round" />

      {/* Point markers */}
      {points.map((p, i) => {
        const isToday = i === todayIndex
        return (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r={isToday ? 5.5 : 3.5}
              fill={isToday ? 'hsl(var(--primary))' : 'var(--of-bg-card)'}
              stroke="hsl(var(--primary))" strokeWidth={2} />
            {isToday && (
              <circle cx={p.x} cy={p.y} r={10}
                fill="hsl(var(--primary))" opacity={0.12} />
            )}
          </g>
        )
      })}

      {/* X-axis labels */}
      {data.map((d, i) => (
        <text
          key={d.day}
          x={xFor(i)}
          y={H - 12}
          textAnchor="middle"
          fontFamily="var(--of-font-mono)"
          fontSize={10.5}
          letterSpacing="0.06em"
          fill={i === todayIndex ? 'hsl(var(--primary))' : 'var(--of-fg-3)'}
          fontWeight={i === todayIndex ? 600 : 400}
        >
          {d.day.toUpperCase()}
        </text>
      ))}
    </svg>
  )
}

/* Tiny sparkline used inside stat cards. Smooth curve, no axes, last point
   highlighted as a dot. */
function Sparkline({ data }: { data: number[] }) {
  const W = 120, H = 36
  if (data.length === 0) {
    return <div style={{ height: H }} />
  }
  const max = Math.max(...data, 1)
  const min = Math.min(...data, 0)
  const range = Math.max(max - min, 1)
  const padY = 4

  const xFor = (i: number) => (data.length === 1 ? W / 2 : (i / (data.length - 1)) * W)
  const yFor = (v: number) => padY + (H - padY * 2) * (1 - (v - min) / range)
  const points = data.map((v, i) => ({ x: xFor(i), y: yFor(v) }))
  const stroke = smoothPath(points)
  const last = points[points.length - 1]

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" aria-hidden>
      <path d={stroke} fill="none" stroke="hsl(var(--primary))" strokeWidth={1.5}
        strokeLinejoin="round" strokeLinecap="round" opacity={0.85} />
      <circle cx={last.x} cy={last.y} r={2.5} fill="hsl(var(--primary))" />
    </svg>
  )
}

/* Donut / completion ring — single arc against a faint track. */
function DonutRing({ value, max, size = 84, stroke = 8 }: { value: number; max: number; size?: number; stroke?: number }) {
  const pct = max <= 0 ? 0 : Math.max(0, Math.min(1, value / max))
  const r = (size - stroke) / 2
  const c = 2 * Math.PI * r
  const dash = c * pct
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
      <circle cx={size / 2} cy={size / 2} r={r}
        fill="none" stroke="var(--of-ink-08)" strokeWidth={stroke} />
      <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
        <circle cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke="hsl(var(--primary))" strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c - dash}`}
          style={{ transition: 'stroke-dasharray 360ms ease-out' }} />
      </g>
      <text x={size / 2} y={size / 2 + 4}
        textAnchor="middle"
        fontFamily="var(--of-font-body)"
        fontSize={size <= 80 ? 16 : 18}
        fontWeight={600}
        fill="var(--of-fg-1)">
        {Math.round(pct * 100)}%
      </text>
    </svg>
  )
}

// ─── Stat cards ───────────────────────────────────────────────────────────────

function StatCard({
  label, value, sub, icon: Icon, accentClassName, children,
}: {
  label: string
  value: React.ReactNode
  sub?: React.ReactNode
  icon: React.ComponentType<{ className?: string }>
  accentClassName?: string
  children?: React.ReactNode
}) {
  return (
    <Card className="bg-card p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <span className={`h-7 w-7 rounded-md flex items-center justify-center ${accentClassName ?? 'bg-primary/10 text-primary'}`}>
          <Icon className="h-3.5 w-3.5" />
        </span>
      </div>
      <div className="flex items-end gap-3">
        <div className="text-3xl font-semibold tracking-tight leading-none text-foreground">{value}</div>
        {children && <div className="flex-1 min-w-0">{children}</div>}
      </div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </Card>
  )
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

export function OrbitFlowDashboard({ stats: rawStats, user, weeklyTime = [], loading }: DashboardProps) {
  const router = useRouter()

  const stats = useMemo<DashStats>(() => ({ ...EMPTY_STATS, ...(rawStats || {}) }), [rawStats])

  const weekly = useMemo(() => weekBuckets(weeklyTime), [weeklyTime])
  const todayIndex = useMemo(() => {
    const d = new Date().getDay()
    return d === 0 ? 6 : d - 1
  }, [])

  const totalHours = useMemo(() => weekly.reduce((a, b) => a + b.h, 0), [weekly])
  const avgHoursPerDay = totalHours / 7
  const totalTasks = stats.assignedIssues + stats.completedThisWeek
  const completionPct = totalTasks > 0 ? Math.round((stats.completedThisWeek / totalTasks) * 100) : 0

  const sparkWeek = weekly.map(w => w.h)
  // Activity counts per day (last 7 entries) — fallback to a flat line.
  const activitySpark = useMemo(() => {
    const acts = (stats.recentActivity || []).slice(0, 14)
    if (acts.length === 0) return [0, 0, 0, 0, 0, 0, 0]
    const buckets = [0, 0, 0, 0, 0, 0, 0]
    acts.forEach((a: any) => {
      const raw = a.created_on || a.createdOn || a.date
      const d = raw ? new Date(raw) : null
      if (!d || isNaN(d.getTime())) return
      const diff = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24))
      const idx = 6 - Math.min(6, Math.max(0, diff))
      buckets[idx]++
    })
    return buckets
  }, [stats.recentActivity])

  const upcoming = (stats.upcomingDeadlines || []).slice(0, 5)
  const activity = (stats.recentActivity || []).slice(0, 5)

  if (loading) {
    return (
      <div className="min-h-screen">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
          <OrbitLoader fullscreen label="Loading your dashboard" />
        </div>
      </div>
    )
  }

  const name = firstNameOf(user)

  return (
    <div className="min-h-screen">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Greeting hero */}
        <PageHeader
          title={greetingTitle(name)}
          subtitle={dateLabel()}
          actions={
            <>
              <Button variant="outline" size="sm" onClick={() => router.push('/time')}>
                <Clock className="h-4 w-4 me-1.5" /> Log time
              </Button>
              <Button size="sm" onClick={() => router.push('/issues/new')}>
                <Plus className="h-4 w-4 me-1.5" /> New task
              </Button>
            </>
          }
        />

        {/* Stat grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="This week"
            value={fmtHours(totalHours)}
            sub="Mon – Sun"
            icon={Clock}
          >
            <Sparkline data={sparkWeek} />
          </StatCard>

          <StatCard
            label="Done"
            value={
              <span className="flex items-end gap-2">
                {stats.completedThisWeek}
                <span className="text-base font-normal text-muted-foreground mb-1">
                  /{totalTasks || 0}
                </span>
              </span>
            }
            sub={`${completionPct}% of ${totalTasks || 0} tasks done`}
            icon={Target}
            accentClassName="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
          >
            <div className="flex justify-end">
              <DonutRing value={stats.completedThisWeek} max={Math.max(totalTasks, 1)} size={56} stroke={6} />
            </div>
          </StatCard>

          <StatCard
            label="Open issues"
            value={stats.assignedIssues}
            sub={
              <span className="flex items-center gap-3">
                <span><span className="text-foreground font-medium">{stats.assignedIssues}</span> assigned</span>
                <span className="h-1 w-1 rounded-full bg-muted-foreground/40" />
                <span><span className="text-foreground font-medium">{stats.completedThisWeek}</span> closed</span>
              </span>
            }
            icon={Flag}
            accentClassName="bg-orange-500/10 text-orange-600 dark:text-orange-400"
          >
            <Sparkline data={activitySpark} />
          </StatCard>

          <StatCard
            label="Hours / day"
            value={`${avgHoursPerDay.toFixed(1)}h`}
            sub={
              <span className="flex items-center gap-1.5">
                <TrendingUp className="h-3 w-3" /> avg this week
              </span>
            }
            icon={CalendarDays}
            accentClassName="bg-violet-500/10 text-violet-600 dark:text-violet-400"
          >
            <Sparkline data={sparkWeek} />
          </StatCard>
        </div>

        {/* Weekly chart */}
        <Card className="bg-card p-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-4">
            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Hours logged this week
              </div>
              <div className="text-2xl font-semibold tracking-tight text-foreground mt-1">
                {fmtHours(totalHours)}
              </div>
            </div>
            <Link
              href="/time"
              className="text-xs text-primary hover:underline inline-flex items-center gap-1"
            >
              Open time tracker <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="-mx-2">
            <AreaChart data={weekly} todayIndex={todayIndex} />
          </div>
        </Card>

        {/* Lower row: Activity + Focus next */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Activity stream */}
          <Card className="bg-card p-6 lg:col-span-3">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="h-7 w-7 rounded-md bg-primary/10 text-primary flex items-center justify-center">
                  <ActivityIcon className="h-3.5 w-3.5" />
                </span>
                <h3 className="text-sm font-semibold text-foreground">Activity stream</h3>
              </div>
              <Link href="/activity" className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1">
                All activity <ArrowRight className="h-3 w-3" />
              </Link>
            </div>

            {activity.length === 0 ? (
              <div className="py-10 text-center">
                <ActivityIcon className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No recent activity yet.</p>
                <p className="text-xs text-muted-foreground/80 mt-0.5">
                  Comments, status changes and time logs will appear here.
                </p>
              </div>
            ) : (
              <ul className="relative space-y-3 before:absolute before:left-[7px] before:top-1 before:bottom-1 before:w-px before:bg-border">
                {activity.map((a: any, i: number) => {
                  const title = a.subject || a.title || a.message || `Activity ${i + 1}`
                  return (
                    <li key={i} className="relative pl-6">
                      <span className="absolute left-[3px] top-1.5 h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-card" />
                      <div className="text-sm text-foreground truncate">{title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {dueLabel(a.created_on || a.createdOn || a.date)}
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </Card>

          {/* Focus next */}
          <Card className="bg-card p-6 lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="h-7 w-7 rounded-md bg-primary/10 text-primary flex items-center justify-center">
                  <Target className="h-3.5 w-3.5" />
                </span>
                <h3 className="text-sm font-semibold text-foreground">Focus next</h3>
              </div>
              {upcoming.length > 0 && (
                <span className="text-xs text-muted-foreground">
                  {upcoming.length} active
                </span>
              )}
            </div>

            {upcoming.length === 0 ? (
              <div className="py-10 text-center">
                <Target className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">All clear — nothing in the queue.</p>
                <Button
                  size="sm" variant="outline"
                  className="mt-4"
                  onClick={() => router.push('/issues')}
                >
                  <Play className="h-3.5 w-3.5 me-1.5" /> Pick a task
                </Button>
              </div>
            ) : (
              <ul className="space-y-2">
                {upcoming.map((t: any, i: number) => {
                  const title = t.subject || t.title || `Task ${t.id}`
                  return (
                    <li key={t.id || i}>
                      <Link
                        href={t.id ? `/issues/${t.id}` : '/issues'}
                        className="group flex items-center gap-3 px-3 py-2.5 -mx-3 rounded-lg hover:bg-muted/40 transition-colors"
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-foreground truncate group-hover:text-primary transition-colors">
                            {title}
                          </div>
                          {t.due_date && (
                            <div className="text-xs text-muted-foreground mt-0.5">
                              Due {dueLabel(t.due_date)}
                            </div>
                          )}
                        </div>
                        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/60 group-hover:text-primary transition-colors flex-shrink-0" />
                      </Link>
                    </li>
                  )
                })}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </div>
  )
}

export default OrbitFlowDashboard
